import { z as DocContext, p as jsxRuntimeExports } from "./doenetml-NmXDuMHr.js";
import React__default, { useContext, useRef } from "react";
import { j as jxg, h as syncLineFamilyVisibility, s as syncLayer, g as syncVisPropValues, e as syncLineStrokeStyle, a as syncWithLabelToggle, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, i as applyLineFamilyLabelPlacement, k as buildLineFamilyLabelAttributes, d as attachLabelHoverHighlight, c as computeLabelMaskCssStyle, l as stabilizeInitialLineFamilyLabelPlacement, r as removeJXGEventHandlers } from "./JSXGraphRenderer-CoUOmMnA.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-iiMuPv8N.js";
import { BoardContext, VERTEX_LAYER_OFFSET, LINE_LAYER_OFFSET } from "./graph-CRde30im.js";
import { u as useSourceNavigation, a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-9gNGzIOR.js";
import { a as buildLineLikeAttributes, c as buildDragHandleAttributes } from "./buildGraphicalAttributes-0RNcbd2S.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { a as resolveLineColor, d as resolveHandleColor } from "./styleColors-Cknqcr0q.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
const lineSegment = React__default.memo(function LineSegment(props) {
  let { componentIdx, id, SVs, actions, sourceOfUpdate, callAction } = useDoenetRenderer(props);
  LineSegment.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  let lineSegmentJXG = useRef(null);
  let point1JXG = useRef(null);
  let point2JXG = useRef(null);
  const dragState = usePointerDragState();
  let previousWithLabel = useRef(null);
  let cancelInitialLabelPlacement = useRef(null);
  let pointCoords = useRef(null);
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  const {
    lastPositionFromCore: lastPositionsFromCore,
    fixed,
    fixLocation
  } = useDraggableRefs(SVs, SVs.numericalEndpoints);
  let endpointsFixed = useRef(false);
  endpointsFixed.current = !SVs.endpointsDraggable || SVs.fixed || SVs.fixLocation;
  const { darkMode } = useContext(DocContext) || {};
  const sourceNavigation = useSourceNavigation(id);
  useBoardPointerTracking(board, dragState);
  useJSXGraphCleanup({
    objectRef: lineSegmentJXG,
    destroy: () => deleteLineSegmentJXG(),
    cancelLabelPlacementRef: cancelInitialLabelPlacement
  });
  function createLineSegmentJXG() {
    if (board === null) {
      return null;
    }
    if (SVs.numericalEndpoints.length !== 2 || SVs.numericalEndpoints.some(
      (x) => x.length !== 2 || x.some((v) => !Number.isFinite(v))
    )) {
      lineSegmentJXG.current = null;
      point1JXG.current = null;
      point2JXG.current = null;
      return;
    }
    const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
    var jsxSegmentAttributes = buildLineLikeAttributes(
      {
        SVs,
        layerOffset: LINE_LAYER_OFFSET,
        fixed: fixed.current,
        fixLocation: fixLocation.current,
        darkMode
      }
    );
    jsxSegmentAttributes.label = buildLineFamilyLabelAttributes({
      labelForGraph: SVs.labelForGraph,
      labelPosition: SVs.labelPosition,
      labelHasLatex: SVs.labelHasLatex,
      applyStyleToLabel: SVs.applyStyleToLabel,
      lineColor,
      layer: SVs.layer,
      maskLabel: SVs.maskLabel
    });
    let endpointsVisible = !endpointsFixed.current && !SVs.hidden;
    let jsxPointAttributes = {
      ...buildDragHandleAttributes({
        elementAttributes: jsxSegmentAttributes,
        layer: 10 * SVs.layer + VERTEX_LAYER_OFFSET,
        darkMode,
        showInfoBox: SVs.showCoordsWhenDragging
      }),
      visible: endpointsVisible
    };
    let endpoints = [
      [...SVs.numericalEndpoints[0]],
      [...SVs.numericalEndpoints[1]]
    ];
    const newPoint1JXG = board.create(
      "point",
      endpoints[0],
      jsxPointAttributes
    );
    const newPoint2JXG = board.create(
      "point",
      endpoints[1],
      jsxPointAttributes
    );
    point1JXG.current = newPoint1JXG;
    point2JXG.current = newPoint2JXG;
    const newSegmentJXG = board.create(
      "segment",
      [newPoint1JXG, newPoint2JXG],
      jsxSegmentAttributes
    );
    lineSegmentJXG.current = newSegmentJXG;
    newSegmentJXG.isDraggable = !fixLocation.current;
    attachLabelHoverHighlight({
      hoverTargetJXG: newSegmentJXG,
      getLabelJXG: () => lineSegmentJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    const segmentBuildCommit = () => {
      if (!pointCoords.current) {
        return null;
      }
      return {
        point1coords: pointCoords.current[0],
        point2coords: pointCoords.current[1]
      };
    };
    attachLineFamilyDragHandlers({
      sourceNavigation,
      jxg: newSegmentJXG,
      tag: 0,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      participatesInDownTag: false,
      suppressClickWhenDownOnOtherTag: true,
      suppressFocusOnDownWhenDownOnOtherTag: true,
      actions: {
        move: actions.moveLineSegment,
        focus: actions.lineSegmentFocused,
        click: actions.lineSegmentClicked
      },
      snapshot: () => [
        [...point1JXG.current.coords.scrCoords],
        [...point2JXG.current.coords.scrCoords]
      ],
      buildTransientMoveArgs: (e, snap) => {
        const next = [];
        if (e.type === "pointermove" && dragState.pointerAtDown.current && snap) {
          for (let j = 0; j < 2; j++) {
            next.push(
              pointerEventToUserCoords(
                e,
                dragState.pointerAtDown.current,
                snap[j],
                board
              )
            );
          }
        } else {
          next.push([
            newSegmentJXG.point1.X(),
            newSegmentJXG.point1.Y()
          ]);
          next.push([
            newSegmentJXG.point2.X(),
            newSegmentJXG.point2.Y()
          ]);
        }
        pointCoords.current = next;
        return {
          point1coords: next[0],
          point2coords: next[1],
          transient: true,
          skippable: true
        };
      },
      buildCommitMoveArgs: () => segmentBuildCommit(),
      onDragApplied: () => {
        newSegmentJXG.point1.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          lastPositionsFromCore.current[0]
        );
        newSegmentJXG.point2.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          lastPositionsFromCore.current[1]
        );
      }
    });
    function attachEndpointHandlers(point, tagN, argKey) {
      attachLineFamilyDragHandlers({
        sourceNavigation,
        jxg: point,
        tag: tagN,
        dragState,
        coordination: dragCoordination,
        componentIdx,
        callAction,
        fixedRef: fixed,
        shouldDispatchFocusOnDown: () => !endpointsFixed.current,
        actions: {
          move: actions.moveLineSegment,
          focus: actions.lineSegmentFocused,
          click: actions.lineSegmentClicked
        },
        snapshot: () => null,
        buildTransientMoveArgs: () => {
          const coords = [point.X(), point.Y()];
          pointCoords.current = coords;
          return {
            [argKey]: coords,
            transient: true,
            skippable: true,
            sourceDetails: { endpoint: tagN }
          };
        },
        buildCommitMoveArgs: () => ({
          [argKey]: pointCoords.current
        }),
        onDragApplied: () => {
          newSegmentJXG.point1.coords.setCoordinates(
            jxg.COORDS_BY_USER,
            lastPositionsFromCore.current[0]
          );
          newSegmentJXG.point2.coords.setCoordinates(
            jxg.COORDS_BY_USER,
            lastPositionsFromCore.current[1]
          );
          if (board) {
            board.updateInfobox(
              tagN === 1 ? newSegmentJXG.point1 : newSegmentJXG.point2
            );
          }
        }
      });
    }
    attachEndpointHandlers(newPoint1JXG, 1, "point1coords");
    attachEndpointHandlers(newPoint2JXG, 2, "point2coords");
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    if (SVs.labelForGraph !== "" && lineSegmentJXG.current.hasLabel) {
      const createdLineSegment = lineSegmentJXG.current;
      cancelInitialLabelPlacement.current = stabilizeInitialLineFamilyLabelPlacement({
        board,
        lineLike: createdLineSegment,
        applyPlacement: (forceFullUpdate) => {
          if (!lineSegmentJXG.current || lineSegmentJXG.current !== createdLineSegment || !lineSegmentJXG.current.hasLabel) {
            return false;
          }
          applyLineFamilyLabelPlacement({
            board,
            lineLike: lineSegmentJXG.current,
            labelPosition: SVs.labelPosition,
            forceFullUpdate
          });
          return true;
        }
      });
    }
    previousWithLabel.current = SVs.labelForGraph !== "";
    return lineSegmentJXG.current;
  }
  function deleteLineSegmentJXG() {
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    if (lineSegmentJXG.current) {
      removeJXGEventHandlers(lineSegmentJXG.current);
      board?.removeObject(lineSegmentJXG.current);
      lineSegmentJXG.current = null;
    }
    if (point1JXG.current) {
      removeJXGEventHandlers(point1JXG.current);
      board?.removeObject(point1JXG.current);
      point1JXG.current = null;
    }
    if (point2JXG.current) {
      removeJXGEventHandlers(point2JXG.current);
      board?.removeObject(point2JXG.current);
      point2JXG.current = null;
    }
  }
  if (board) {
    if (lineSegmentJXG.current === null) {
      createLineSegmentJXG();
    } else if (SVs.numericalEndpoints.length !== 2 || SVs.numericalEndpoints.some(
      (x) => x.length !== 2 || x.some((v) => !Number.isFinite(v))
    )) {
      deleteLineSegmentJXG();
    } else if (point1JXG.current && point2JXG.current) {
      let validCoords = true;
      for (let coords of [
        SVs.numericalEndpoints[0],
        SVs.numericalEndpoints[1]
      ]) {
        if (!Number.isFinite(coords[0])) {
          validCoords = false;
        }
        if (!Number.isFinite(coords[1])) {
          validCoords = false;
        }
      }
      lineSegmentJXG.current.point1.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalEndpoints[0]
      );
      lineSegmentJXG.current.point2.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalEndpoints[1]
      );
      if (sourceOfUpdate.sourceInformation && componentIdx in sourceOfUpdate.sourceInformation) {
        let sourceInfo = sourceOfUpdate.sourceInformation[componentIdx];
        if (typeof sourceInfo === "object" && sourceInfo) {
          let ind = sourceInfo.endpoint;
          if (ind === 1) {
            board.updateInfobox(lineSegmentJXG.current.point1);
          } else if (ind === 2) {
            board.updateInfobox(lineSegmentJXG.current.point2);
          }
        }
      }
      let visible = !SVs.hidden && validCoords;
      syncLineFamilyVisibility(
        lineSegmentJXG.current,
        visible,
        validCoords
      );
      let endpointsVisible = !endpointsFixed.current && visible;
      point1JXG.current.visProp["visible"] = endpointsVisible;
      point1JXG.current.visPropCalc["visible"] = endpointsVisible;
      point2JXG.current.visProp["visible"] = endpointsVisible;
      point2JXG.current.visPropCalc["visible"] = endpointsVisible;
      point1JXG.current.visProp.showinfobox = SVs.showCoordsWhenDragging;
      point2JXG.current.visProp.showinfobox = SVs.showCoordsWhenDragging;
      lineSegmentJXG.current.visProp.fixed = fixed.current;
      lineSegmentJXG.current.visProp.highlight = !fixLocation.current;
      lineSegmentJXG.current.isDraggable = !fixLocation.current;
      if (syncLayer(lineSegmentJXG.current, SVs.layer, LINE_LAYER_OFFSET)) {
        const vertexLayer = 10 * SVs.layer + VERTEX_LAYER_OFFSET;
        point1JXG.current.setAttribute({ layer: vertexLayer });
        point2JXG.current.setAttribute({ layer: vertexLayer });
      }
      const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
      const handleColor = resolveHandleColor(darkMode);
      syncVisPropValues(point1JXG.current, {
        highlightfillcolor: handleColor
      });
      syncVisPropValues(point2JXG.current, {
        highlightfillcolor: handleColor
      });
      syncLineStrokeStyle(lineSegmentJXG.current, {
        lineColor,
        lineWidth: SVs.selectedStyle.lineWidth,
        lineOpacity: SVs.selectedStyle.lineOpacity,
        dash: styleToDash(SVs.selectedStyle.lineStyle)
      });
      lineSegmentJXG.current.name = SVs.labelForGraph;
      syncWithLabelToggle(
        lineSegmentJXG.current,
        SVs.labelForGraph,
        previousWithLabel
      );
      if (point1JXG.current.highlighted) {
        board.updateInfobox(point1JXG.current);
      } else if (point2JXG.current.highlighted) {
        board.updateInfobox(point2JXG.current);
      }
      lineSegmentJXG.current.needsUpdate = true;
      lineSegmentJXG.current.update();
      if (lineSegmentJXG.current.hasLabel && lineSegmentJXG.current.label) {
        const label = lineSegmentJXG.current.label;
        label.needsUpdate = true;
        syncLabelStrokeColor(label, SVs.applyStyleToLabel, lineColor);
        syncLabelMaskCssStyle(label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        applyLineFamilyLabelPlacement({
          board,
          lineLike: lineSegmentJXG.current,
          labelPosition: SVs.labelPosition
        });
      }
      point1JXG.current.needsUpdate = true;
      point1JXG.current.update();
      point2JXG.current.needsUpdate = true;
      point2JXG.current.update();
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
  }
  if (SVs.hidden) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
});
export {
  lineSegment as default
};
